import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import { useFragment } from "react-relay";

import { setRequestGroupCountAtom } from "@/features/BookingWizard/atoms/actions/setRequestGroupCount/setRequestGroupCountAtom";
import { BookingWizardDataAtom } from "@/features/BookingWizard/atoms/stores";
import { useGetTimeOptions } from "@/features/BookingWizard/hooks/useGetTimeOptions/useGetTimeOptions";
import { useFilterRequestStartTimeSlots } from "@/hooks/useFilterRequestStartTimeSlots/useFilterRequestStartTimeSlots";
import { useFlags } from "@/utils/launchDarkly";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import {
  MultiCourseRequestSelector,
  RequestCoursePreferenceInput,
} from "@whoosh/features/src/MultiCourseRequestSelector";

import { RequestBookingDetailsStepNode$key } from "./__generated__/RequestBookingDetailsStepNode.graphql";
import { RequestGroupSizeControl } from "./controls/GroupSizeControl/RequestGroupSizeControl";
import { HolesControl } from "./controls/HolesControl";
import { MultipleSlotRequestGroupCountControl } from "./controls/MultipleSlotRequestGroupCountControl/MultipleSlotRequestGroupCountControl";
import { RecurringRequestControls } from "./controls/RecurringRequestControls/RecurringRequestControls";
import { TimeSelectionControl } from "./controls/TimeSelectionControl/TimeSelectionControl";

type TimeSlotNode = {
  readonly id: string;
  readonly time: string;
  readonly availability?: string | null;
} | null;

type Props = {
  nodeRef: RequestBookingDetailsStepNode$key | null;
};

export const RequestBookingDetailsStep = ({ nodeRef }: Props) => {
  const [bookingWizardData, setBookingWizardData] = useAtom(
    BookingWizardDataAtom
  );

  const node = useFragment(RequestBookingDetailsStepFacilityFragment, nodeRef);
  const facility = node?.agenda?.facility;
  const { patronRecurringRequests } = useFlags();
  const setRequestGroupCount = useSetAtom(setRequestGroupCountAtom);

  const isPrivate = facility?.club?.supportsPrivate ?? false;

  // Extract courses and course buckets for multi-course selector
  const courses = useMemo(() => {
    return (
      facility?.courses?.edges
        ?.map((edge) => edge?.node)
        .filter(
          (n): n is { readonly id: string; readonly name: string } => !!n
        ) ?? []
    );
  }, [facility?.courses?.edges]);

  const courseBuckets = useMemo(() => {
    return (
      facility?.courseBuckets?.edges
        ?.map((edge) => edge?.node)
        .filter((n): n is NonNullable<typeof n> => !!n)
        .map((bucket) => ({
          id: bucket.id,
          name: bucket.name,
          courses: (bucket.courses ?? [])
            .filter(
              (c): c is { readonly id: string; readonly name: string } => !!c
            )
            .map((c) => ({ id: c.id, name: c.name })),
        })) ?? []
    );
  }, [facility?.courseBuckets?.edges]);

  const showCourseSelector = courses.length >= 2;

  const courseCopyOverrides = useMemo(() => {
    const overrides = facility?.copyOverrides?.course;
    if (!overrides?.singular && !overrides?.plural) return undefined;
    return {
      singular: overrides?.singular ?? undefined,
      plural: overrides?.plural ?? undefined,
    };
  }, [facility?.copyOverrides?.course]);

  const handleCoursePreferencesChange = useCallback(
    (preferences: RequestCoursePreferenceInput[]) => {
      if (bookingWizardData?.type !== "RequestBooking") return;
      setBookingWizardData({
        ...bookingWizardData,
        requestCoursePreferences: preferences,
      });
    },
    [bookingWizardData, setBookingWizardData]
  );

  // Extract time slots from the agenda
  const agendaTimeSlots = useMemo<TimeSlotNode[]>(() => {
    if (!node?.agenda?.timeSlots?.edges) return [];
    return node.agenda.timeSlots.edges
      .map((edge) => edge?.node || null)
      .filter((node) => !!node);
  }, [node?.agenda?.timeSlots?.edges]);

  const timeSlots = useFilterRequestStartTimeSlots(agendaTimeSlots);

  // Get time options for the default time effect
  const timeOptions = useGetTimeOptions(timeSlots);
  // Set default times if not already set
  useEffect(() => {
    if (
      bookingWizardData?.type === "RequestBooking" &&
      (!bookingWizardData.minTime || !bookingWizardData.maxTime) &&
      timeOptions.length > 0
    ) {
      const defaultMinTime = bookingWizardData.minTime || timeOptions[0]?.value;
      const defaultMaxTime =
        bookingWizardData.maxTime || timeOptions[timeOptions.length - 1]?.value;

      setBookingWizardData({
        ...bookingWizardData,
        minTime: defaultMinTime,
        maxTime: defaultMaxTime,
      });
    }
  }, [bookingWizardData, setBookingWizardData, timeOptions]);

  // Only render for request bookings
  if (bookingWizardData?.type !== "RequestBooking") {
    return null;
  }

  return (
    <VStack gap={2} px={3}>
      <TimeSelectionControl timeSlots={timeSlots} />

      <MultipleSlotRequestGroupCountControl
        currentGroupCount={bookingWizardData.requestGroupCount ?? 1}
        maxGroupCount={bookingWizardData.maxRequestGroupCount ?? 1}
        setGroupCount={setRequestGroupCount}
      />

      {!isPrivate ? (
        <>
          <Box bg="surface-primary" borderRadius="4">
            <RequestGroupSizeControl nodeRef={node ?? null} />
          </Box>

          <HolesControl facility={facility} />
        </>
      ) : null}

      {showCourseSelector ? (
        <Box bg="surface-primary" borderRadius="4">
          <MultiCourseRequestSelector
            courses={courses}
            courseBuckets={courseBuckets}
            value={bookingWizardData.requestCoursePreferences}
            onChange={handleCoursePreferencesChange}
            copyOverrides={courseCopyOverrides}
          />
        </Box>
      ) : null}

      {patronRecurringRequests &&
      isPrivate &&
      !facility?.club?.supportsPublic &&
      facility?.type !== "DINING" &&
      facility?.privateBookingPermissionSet?.membersCanMakeRecurringRequests &&
      node?.agenda?.date &&
      facility.patronRecurringRequestMaximumEndDate &&
      facility.patronRecurringRequestMaximumEndDate > node.agenda.date ? (
        <RecurringRequestControls
          clubWeekStartsOn={
            facility.club?.calendarStartDay === "SUNDAY" ? 0 : 1
          }
          sourceDate={node.agenda.date}
          maximumEndDate={facility.patronRecurringRequestMaximumEndDate}
        />
      ) : null}
    </VStack>
  );
};

export const RequestBookingDetailsStepFacilityFragment = graphql`
  fragment RequestBookingDetailsStepNode on Node {
    ...RequestGroupSizeControlNode
    ... on QueueSlot {
      id
      capacity(bookingType: STANDARD)
      agenda {
        id
        date
        timeSlots(first: 1000) {
          edges {
            node {
              id
              time
              availability
            }
          }
        }
        facility {
          club {
            calendarStartDay
            supportsPrivate
            supportsPublic
          }
          id
          type
          lessonDurationMins
          patronRecurringRequestMaximumEndDate
          privateBookingPermissionSet {
            membersCanMakeRecurringRequests
          }
          maxMultipleBookings
          multipleBookingsEnabled
          copyOverrides {
            course {
              singular
              plural
            }
          }
          courses {
            edges {
              node {
                id
                name
              }
            }
          }
          courseBuckets(first: 100) {
            edges {
              node {
                id
                name
                courses {
                  id
                  name
                }
              }
            }
          }
          courseLayouts {
            edges {
              node {
                id
                code
                name
                enabled
                holeCount
                memberVisible
              }
            }
          }
        }
      }
    }
  }
`;
